import { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";

export interface TemplateContext {
  first_name?: string;
  firstName?: string;
  firstname?: string;
  [key: string]: string | undefined;
}

/**
 * Replaces template variables in a string with actual values
 * @param template - The template string containing variables like {{first_name}}
 * @param context - Object containing the values to substitute
 * @returns The string with all template variables replaced
 */
export function replaceTemplateVariables(template: string, context: TemplateContext): string {
  const result = template.replace(/\{\{(\w+)\}\}/g, (_, variable) => {
    const value = context[variable];
    // If value exists and is not empty, use it
    // Otherwise return empty string to remove the placeholder
    return value && value.trim() !== "" ? value : "";
  });
  return result.replace(/ -- /g, " \u2014 ");
}

/**
 * Replaces template variables specifically for bucket content
 * Supports multiple variations of name fields:
 * - {{first_name}} / {{firstName}} / {{firstname}} - First name variations
 * - {{sur_name}} / {{surName}} / {{surname}} - Surname variations
 * - {{display_name}} / {{displayName}} / {{displayname}} - Display name variations
 * - {{full_name}} / {{fullName}} / {{fullname}} - Full name variations (prioritizes Supabase)
 *
 * @param content - The bucket content template (name or description)
 * @param deceasedUserData - The deceased user data from useDeceasedUserData hook
 * @returns The content with template variables replaced
 */
export function replaceBucketContent(
  content: string | null | undefined,
  deceasedUserData: DeceasedUserData | null,
): string {
  // Handle null/undefined content
  if (content == null) {
    return "";
  }

  // Always try to replace, even if firstName is empty
  // This will at least remove the placeholders
  if (!deceasedUserData) {
    return content;
  }

  const result = replaceTemplateVariables(content, {
    // First name variations
    first_name: deceasedUserData.firstName,
    firstName: deceasedUserData.firstName,
    firstname: deceasedUserData.firstName,
    // Surname variations
    sur_name: deceasedUserData.surName,
    surName: deceasedUserData.surName,
    surname: deceasedUserData.surName,
    // Display name variations
    display_name: deceasedUserData.displayName,
    displayName: deceasedUserData.displayName,
    displayname: deceasedUserData.displayName,
    // Full name variations (prioritizes Supabase data)
    full_name: deceasedUserData.fullName,
    fullName: deceasedUserData.fullName,
    fullname: deceasedUserData.fullName,
  });

  return result;
}
